import { LabeledContainer, Select } from '@schibsted-svp/react-ui';
import type { DefaultOptionType, ExtendedProps as SelectProps } from '@schibsted-svp/react-ui/lib/es/Select';
import type { Category } from '@schibsted-svp/svp-api-types';
import { findSingleOption, getValueFromOption } from 'components/ui/Form/Select';
import Tooltip from 'components/ui/Tooltip';
import { generateSelectOptionsWithDepth, getBreadCrumbs, getParentCategoriesBreadCrumbsAsString } from 'lib/categories';
import type { Control, ControllerRenderProps, FieldPath, FieldValues, UseFormGetValues } from 'react-hook-form';
import { useController } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { getCategoriesItems } from 'store/categories/selectors';
import css from './CategorySelect.module.scss';

type CategorySelectWrapper = {
    provider: string;
    getValues: UseFormGetValues<FieldValues>;
    categories: Category[];
    useWrapper?: boolean;
    children?: React.ReactNode;
};

function CategorySelectWrapper({ provider, getValues, categories, useWrapper, children }: CategorySelectWrapper) {
    const categoryId = getValues?.()?.category?.id;
    const parentCategories = getParentCategoriesBreadCrumbsAsString(categoryId, categories);

    if (useWrapper) {
        return (
            <LabeledContainer
                strict
                label={
                    <>
                        Select category
                        {categoryId ? (
                            <Tooltip
                                theme="light"
                                content={`${parentCategories.length ? 'Parent category: ' : ''} ${parentCategories}`}
                                placement="top"
                                offset={[0, 12]}
                            >
                                <Link to={`/${provider}/category/${categoryId}`} className={css.categoryLabel}>
                                    (current ID {categoryId})
                                </Link>
                            </Tooltip>
                        ) : null}
                    </>
                }
            >
                {' '}
                {children}
            </LabeledContainer>
        );
    }

    return children;
}

type CategorySelectProps<TFieldValues extends FieldValues, IsMulti extends boolean> = {
    name: FieldPath<TFieldValues>;
    control: Control<TFieldValues>;
    provider: string;
    getValues: UseFormGetValues<TFieldValues>;
    filterIds?: number[];
    normalizeValue?: boolean;
} & SelectProps<DefaultOptionType, IsMulti>;

export function CategorySelect<TFieldValues extends FieldValues, IsMulti extends boolean>({
    name,
    control,
    provider,
    getValues,
    filterIds = [],
    normalizeValue = true,
    useWrapper = false,
    ...rest
}: CategorySelectProps<TFieldValues, IsMulti>) {
    const { field, fieldState } = useController({ name, control });

    const categories = useSelector((state) => getCategoriesItems(state, { provider }));
    const options = generateSelectOptionsWithDepth(categories).filter(({ value }) => !filterIds.includes(value));

    const customStyles: SelectProps<DefaultOptionType, IsMulti>['styles'] = {
        option: (base, { data }) => ({
            ...base,
            paddingLeft: `${data.depth * 10}px !important`,
            '&:hover': {
                fontWeight: 600,
            },
            [`&:hover .${css.breadcrumbs}`]: {
                maxHeight: '30px',
                pointerEvents: 'auto',
                opacity: 1,
                marginBottom: '5px',
            },
        }),
    };

    const normalizeValueHandlers: Pick<ControllerRenderProps, 'value' | 'onChange'> = {
        value: findSingleOption(options)(field.value),
        onChange: (value) => field.onChange(getValueFromOption(value)),
    };

    return (
        <CategorySelectWrapper
            provider={provider}
            getValues={getValues}
            categories={categories}
            useWrapper={useWrapper}
        >
            <Select
                {...field}
                {...(normalizeValue && normalizeValueHandlers)}
                options={options}
                styles={customStyles}
                isLoading={options.length === 0}
                backspaceRemovesValue={false}
                splitButton={false}
                size="small"
                error={fieldState.error?.message}
                formatOptionLabel={({ value, label, showCategory }, { context }) => {
                    const breadcrumbs = getBreadCrumbs(value, categories);
                    breadcrumbs.pop();
                    const breadCrumbsNames = breadcrumbs.map(({ title }) => title).join(' / ');

                    return (
                        <span className={css.categoryNameContainer}>
                            {context === 'menu' && <span className={css.breadcrumbs}>{breadCrumbsNames}</span>}
                            <span
                                className={css.categoryName}
                                style={{ textDecoration: showCategory ? 'none' : 'line-through' }}
                                data-value={value}
                            >
                                {label}
                            </span>
                        </span>
                    );
                }}
                {...rest}
            />
        </CategorySelectWrapper>
    );
}
